import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_ROWS } from "@/content/site";
import { useMedia, useMediaSlots } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

const ServiceMedia = ({ slot, label }) => {
  const { items, hasReal } = useMedia(slot, 0);
  if (!hasReal) return null;
  return (
    <>
      <MediaFrame item={items[0]} ratio="aspect-[4/5]" autoplay hoverPlay={false} dark className="rounded-sm" />
      {label && <p className="mt-4 label-eyebrow text-[#FAF7F2]/40">{label}</p>}
    </>
  );
};

const ServicesEditorial = () => {
  const [active, setActive] = useState(0);
  const current = SERVICE_ROWS[active];
  const { slots } = useMediaSlots();
  const anyMedia = SERVICE_ROWS.some((s) => (slots[s.slot] || []).length > 0);

  return (
    <section id="services" className="py-32 md:py-48 bg-[#2E1B16] text-[#FAF7F2]" data-testid="services" aria-labelledby="services-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16 md:mb-24">
          <Reveal><Eyebrow>What we do</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 id="services-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95]">
              We don't sell activities.<br />
              We build <span className="font-accent font-normal normal-case text-[#38BDF8] text-[1.1em]">outcomes.</span>
            </h2>
          </Reveal>
        </div>

        <div className={`grid gap-12 lg:gap-20 items-start ${anyMedia ? "lg:grid-cols-[1.3fr_0.7fr]" : ""}`}>
          <ol className="border-t border-[#FAF7F2]/12" data-testid="service-rows">
            {SERVICE_ROWS.map((s, i) => {
              const on = active === i;
              return (
                <li key={s.key} className="border-b border-[#FAF7F2]/12">
                  <div
                    role="button" tabIndex={0}
                    onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActive(i)}
                    data-testid={`service-row-${s.key}`} aria-expanded={on}
                    className="group w-full text-left py-7 md:py-9 grid grid-cols-[3rem_1fr] md:grid-cols-[4rem_1fr] gap-x-4 cursor-pointer"
                  >
                    <span className={`font-body text-sm pt-2 transition-colors ${on ? "text-[#38BDF8]" : "text-[#FAF7F2]/35"}`}>{s.n}</span>
                    <div>
                      <h3 className={`font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight transition-colors duration-500 ${on ? "text-[#FAF7F2]" : "text-[#FAF7F2]/35 group-hover:text-[#FAF7F2]/70"}`}>
                        {s.title}
                      </h3>
                      <AnimatePresence initial={false}>
                        {on && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                            <p className="mt-4 text-[#FAF7F2]/65 text-lg leading-relaxed max-w-xl">{s.blurb}</p>
                            <div className="lg:hidden mt-6 w-40"><ServiceMedia slot={s.slot} /></div>
                            <Link to={s.to} data-testid={`service-link-${s.key}`} className="mt-6 inline-flex items-center gap-2 text-[#38BDF8] font-semibold hover:text-[#FAF7F2] transition-colors">
                              More on {s.title.toLowerCase()} <ArrowUpRight size={18} />
                            </Link>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {anyMedia && (
            <div className="hidden lg:block sticky top-28">
              <AnimatePresence mode="wait">
                <motion.div key={current.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                  <ServiceMedia slot={current.slot} label={`${current.n} · ${current.title}`} />
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesEditorial;
